// UserGroupsView.jsx
import { Box } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import SearchInput from "../../../../../../components/filters/SearchInput";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { useFileNaming } from "../../../../../../hooks/useFileNaming";
import IntegrationImage from "../../../../integrations/components/IntegrationImage";
import TextIntegrationStyle from "../../TextIntegrationStyle";
import { getUserGroupsRows } from "./checkRowsUserData";

const UserGroupsView = ({ entityData }) => {
  const { trackEvent } = useAPI();
  const { generateBreezeFileName } = useFileNaming();
  const gridRef = useGridApiRef();

  const [allGroups, setAllGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });

  useEffect(() => {
    const groups = getUserGroupsRows(entityData);
    setAllGroups(groups);
  }, [entityData]);

  useEffect(() => {
    if (appliedFilters.searchString !== "") {
      const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
      const filteredRows = allGroups.filter(
        (row) =>
          row?.group_name?.toLowerCase().includes(lowerCaseSearchString) ||
          row?.group_type?.toLowerCase().includes(lowerCaseSearchString) ||
          row?.source?.toLowerCase().includes(lowerCaseSearchString)
      );
      setFilteredGroups(filteredRows);
    } else {
      setFilteredGroups(allGroups);
    }
  }, [appliedFilters.searchString, allGroups]);

  const handleTableExport = () => {
    trackEvent(
      `User clicked export button for user groups - ${entityData?.display_name}`
    );
    const userName =
      entityData?.display_name?.replace(/[^a-zA-Z0-9]/g, "_") || "user";
    const csvExportOptions = {
      fileName: generateBreezeFileName(`user_${userName}_groups`),
    };
    gridRef.current?.exportDataAsCsv(csvExportOptions);
  };

  const columnsToShow = [
    {
      field: "source",
      headerName: "Source",
      flex: 0.5,
      renderCell: (params) => {
        return (
          <IntegrationImage
            name={params?.value}
            width={"24px"}
            style={{ margin: "16px" }}
            newLogo={true}
          />
        );
      },
    },
    {
      field: "group_name",
      headerName: "Name",
      flex: 1,
      renderCell: (params) => {
        return <TextIntegrationStyle title={""} value={params?.value} />;
      },
    },
    {
      field: "group_type",
      headerName: "Type",
      flex: 1,
      renderCell: (params) => {
        return <TextIntegrationStyle title={""} value={params?.value} />;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "300px" }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <ExportButton
          handleExport={handleTableExport}
          disabled={!filteredGroups || filteredGroups.length === 0}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StyledDataGrid
          apiRef={gridRef}
          columns={columnsToShow}
          rows={filteredGroups}
        />
      </Box>
    </>
  );
};

export default UserGroupsView;
